import { MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import type {
  Coding,
  Condition,
  MedicationRequest,
  Observation,
  Patient,
  PlanDefinition,
  Resource,
  ValueSet,
} from '@medplum/fhirtypes';
import { codingSatisfies } from '../matching';
import { differenceInYears, parseISO } from 'date-fns';
import { leafActions } from './planDefinition';
import { valueSetRxcuis } from '../valueSets/rxNormDrugClasses';
import type { ContextQuery, SearchFn } from './types';

/** Results of the context queries, keyed by query name. */
export type ContextResources = Record<string, Resource[]>;

/** The one query every protocol needs. */
export const patientQuery: ContextQuery = {
  name: 'patient',
  resourceType: 'Patient',
  params: (patientRef) => ({ _id: patientRef.replace(/^Patient\//, '') }),
};

export const activeMedicationRequestsQuery: ContextQuery = {
  name: 'activeMedicationRequests',
  resourceType: 'MedicationRequest',
  params: (patientRef) => ({ patient: patientRef, status: 'active', _count: '100' }),
};

export const activeConditionsQuery: ContextQuery = {
  name: 'activeConditions',
  resourceType: 'Condition',
  params: (patientRef) => ({ patient: patientRef, 'clinical-status': 'active,recurrence,relapse', _count: '100' }),
};

/** Latest Observation for one code, from any source — the evidence that anchors a cadence. */
/** Results key for the latest-Observation query of one coding — namespaced by system, so two systems sharing a code string cannot collide. */
export const evidenceKey = (system: string, code: string): string => `latest:${system}|${code}`;

export const latestObservationQuery = (system: string, code: string): ContextQuery => ({
  name: evidenceKey(system, code),
  resourceType: 'Observation',
  params: (patientRef) => ({ patient: patientRef, code: `${system}|${code}`, _sort: '-date', _count: '1' }),
});

/**
 * Every result code a protocol's actions track gets a latest-Observation query, so the planner can
 * anchor each cadence on real evidence rather than on when the protocol was applied.
 */
export const evidenceQueriesFor = (planDefinition: PlanDefinition): ContextQuery[] => {
  const seen = new Set<string>();
  const queries: ContextQuery[] = [];
  for (const action of leafActions(planDefinition)) {
    const coding = action.code?.[0]?.coding?.[0];
    if (!coding?.code || !coding.system) continue;
    const key = evidenceKey(coding.system, coding.code);
    if (seen.has(key)) continue;
    seen.add(key);
    queries.push(latestObservationQuery(coding.system, coding.code));
  }
  return queries;
};

export const loadContextResources = async (
  search: SearchFn,
  patientRef: string,
  queries: ContextQuery[],
): Promise<ContextResources> => {
  const entries = await Promise.all(
    queries.map(async (q) => {
      const found = await search(q.resourceType, q.params(patientRef));
      // A search is answered by the server, but the results are keyed by the query's own name and read
      // by helpers that assume the type that query asked for. Keeping only matching resources makes
      // `resources.activeConditions` hold Conditions and nothing else, so readers cast rather than filter.
      return [q.name, found.filter((r) => r.resourceType === q.resourceType)] as const;
    }),
  );
  return Object.fromEntries(entries);
};

// --- derivations shared by protocols -------------------------------------------------------------------

export const patientOf = (resources: ContextResources): Patient | undefined =>
  resources.patient?.[0] as Patient | undefined;

export const latestObservation = (resources: ContextResources, system: string, code: string): Observation | undefined =>
  resources[evidenceKey(system, code)]?.[0] as Observation | undefined;

/** The instant an Observation is about, for anchoring: `effectiveDateTime`, else the period start, else `issued`. */
export const observationInstant = (observation: Observation | undefined): string | undefined =>
  observation?.effectiveDateTime ?? observation?.effectivePeriod?.start ?? observation?.issued;

export const ageYears = (patient: Patient | undefined, now: Date): number | undefined =>
  patient?.birthDate ? differenceInYears(now, parseISO(patient.birthDate)) : undefined;

/**
 * The active MedicationRequests whose RxNorm coding is in the ValueSet.
 *
 * Compares codes directly, which works because the drug-class ValueSets hold ingredient RxCUIs and
 * a medication arrives carrying its ingredient codings alongside its product one — resolved by the
 * caller before planning, so this stays pure (`withIngredientCodings` in `@liza/services`).
 */
export const medicationRequestsIn = (resources: ContextResources, valueSet: ValueSet): MedicationRequest[] => {
  const rxcuis = new Set(valueSetRxcuis(valueSet));
  return ((resources.activeMedicationRequests as MedicationRequest[] | undefined) ?? []).filter((m) =>
    (m.medicationCodeableConcept?.coding ?? []).some(
      (c) => c.system === MEDICATION_CODE_SYSTEMS.RXNORM && c.code !== undefined && rxcuis.has(c.code),
    ),
  );
};

/** The earliest `authoredOn` among the requests — when the patient started on that class. */
export const earliestAuthoredOn = (requests: MedicationRequest[]): string | undefined =>
  requests
    .map((m) => m.authoredOn)
    .filter((d): d is string => !!d)
    .sort()[0];

/**
 * Active Conditions matching any of the wanted codings. ICD-10-CM matches as a prefix, so `E11.4`
 * covers the category; every other system matches exactly.
 *
 * System-qualified rather than bare prefixes because a diagnosis reaches the chart in whichever
 * system its source used: an imported record frequently carries SNOMED alone, and an ICD-10-only
 * read made a conditional action take the wrong branch on a chart that plainly had the condition
 * (#10129).
 */
export const conditionsMatching = (resources: ContextResources, wanted: readonly Coding[]): Condition[] =>
  ((resources.activeConditions as Condition[] | undefined) ?? []).filter((c) =>
    (c.code?.coding ?? []).some((coding) => wanted.some((w) => codingSatisfies(coding, w))),
  );
